/**
 * The wall in front of the dashboard when an organisation demands two-factor.
 *
 * WHAT THIS IS NOT. It is not a security control - anybody who wanted to could
 * skip a client-side gate. The account is already signed in by the time this
 * renders, and Firestore rules, not this component, are what stop a session
 * reading anything. This exists to make a POLICY land: an org that has decided
 * everyone carries a second factor should not have to chase people, and the
 * moment somebody will actually do it is when the dashboard is otherwise in
 * their way.
 *
 * WHY IT DOES NOT SIGN THEM OUT. Enrolling requires a signed-in session -
 * Firebase mints the TOTP secret against the current user. Kicking them back to
 * the sign-in form would make the requirement impossible to satisfy.
 *
 * THE WAY BACK, which matters more than the wall:
 *   - the requirement cannot be switched on unless the project can actually
 *     enrol people, checked server-side in setOrgSecurity;
 *   - somebody who loses their authenticator is cleared by anyone holding
 *     security.manage, and a platform admin can clear the last owner;
 *   - Sign out is always available, so a shared machine is never stuck here.
 */

import { useState } from 'react';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { enrolledFactors } from '../lib/mfa';
import { MfaCard } from './MfaCard';
import { Wordmark } from '../brand';

export function MfaRequired({ orgName, onEnrolled }: { orgName: string; onEnrolled: () => void }) {
  const [checkError, setCheckError] = useState<string | null>(null);

  function recheck() {
    // Read the factors off the live user object rather than trusting a callback
    // from the card: the enrolment happens inside the SDK, and this is the same
    // question the gate itself asks.
    if (enrolledFactors(auth.currentUser).length > 0) {
      onEnrolled();
      return;
    }
    setCheckError('No authenticator is enrolled yet. Finish the steps above, then press this again.');
  }

  return (
    <div className="mfagate">
      <div className="mfagate-card">
        <div className="authaction-brand">
          <Wordmark width={168} />
        </div>

        <h1 className="authaction-heading">{orgName} requires two-factor authentication</h1>
        <p className="authaction-detail">
          Set up an authenticator app to carry on. It takes about a minute, and you will only be
          asked for a code when you sign in.
        </p>

        {/* canManage false: this is a person enrolling themselves, and the
            project-level switches on this card are none of their business. */}
        <MfaCard orgId={null} canManage={false} />

        {checkError && <p className="authaction-error">{checkError}</p>}

        <div className="mfagate-actions">
          <button className="authaction-button" type="button" onClick={recheck}>
            I have set it up
          </button>
          <button
            className="btn-link"
            type="button"
            onClick={() => void signOut(auth)}
            style={{ marginLeft: 14 }}
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
